import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AuthCredential, useAuth, useUser } from '@dfl/react-security';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { loginSchema } from 'modules/authentication/schemas/login.schema';
import { getDeviceId } from 'modules/notification/services/device-id.service';
import MfaService from 'modules/authentication/services/mfa.service';
import {
  AuthTokens,
  isMfaRequiredResponse,
  LoginResponse,
  MfaRequiredResponse,
} from 'modules/authentication/services/mfa.types';

const useLoginForm = () => {
  const { isLoading: isLoadingUser, hasToken } = useUser();
  const { setAuth } = useAuth();
  const [mfaChallenge, setMfaChallenge] = useState<MfaRequiredResponse>();
  const [mfaError, setMfaError] = useState<string>();
  const [isCompletingMfa, setIsCompletingMfa] = useState(false);

  const { register, control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      space: null,
      remember: false,
    },
  });

  const { mutateAsync, error, isLoading } = useMutation(MfaService.adminLogin);

  const completeLoginResponse = useCallback((response: LoginResponse) => {
    setMfaError(undefined);
    if (isMfaRequiredResponse(response)) {
      setMfaChallenge(response);
      return;
    }
    setAuth(response);
  }, [setAuth]);

  const clearMfaChallenge = useCallback(() => {
    setMfaChallenge(undefined);
  }, []);

  const failMfaChallenge = useCallback(() => {
    setMfaChallenge(undefined);
    setMfaError('authentication:mfa.sessionExpired');
  }, []);

  // Loading the profile is the only step left between a verified code and the dashboard. If it
  // fails, the library drops the token it was just given and the user would sit on a spinner
  // forever, so fall back to a clean login.
  //
  // `hasToken` is read from storage on every provider render, so right after `setAuth` it is
  // still false for a render or two. Only a token that was seen and then vanished means the
  // session was really rejected; checking `!hasToken` alone rejects every valid login.
  const tokenSeenRef = useRef(false);
  useEffect(() => {
    if (!isCompletingMfa) {
      tokenSeenRef.current = false;
      return;
    }
    if (hasToken) {
      tokenSeenRef.current = true;
      return;
    }
    if (tokenSeenRef.current && !isLoadingUser) {
      setIsCompletingMfa(false);
      setMfaChallenge(undefined);
      setMfaError('authentication:mfa.errors.generic');
    }
  }, [isCompletingMfa, isLoadingUser, hasToken]);

  return {
    control,
    register,
    error,
    isLoading: isLoading || isLoadingUser,
    setValue,
    mfaChallenge,
    mfaError,
    isCompletingMfa,
    clearMfaChallenge,
    failMfaChallenge,
    completeLoginResponse,
    completeMfaLogin: (tokens: AuthTokens) => {
      // The challenge is kept on purpose: clearing it here would drop back to the login form
      // for as long as `/me` is in flight, which reads as being thrown out after a valid code.
      setIsCompletingMfa(true);
      setAuth(tokens);
    },
    onSubmit: handleSubmit(async (value) => {
      try {
        setMfaError(undefined);
        const selectedSpace = value.space as { identifier?: string } | string | null;
        const credentials: AuthCredential & { deviceId: string; space: string | null } = {
          ...value,
          identifier: value.email,
          deviceId: getDeviceId(),
          space: (typeof selectedSpace === 'object' ? selectedSpace?.identifier : selectedSpace) || null,
        };
        const response = await mutateAsync(credentials);
        completeLoginResponse(response);
        // go to the previews page

        // const from = location.state?.from?.pathname || "/";
        // navigate(from, {replace: true});
        // navigate('/', { replace: true });
      } catch (e) {}
    }),
  };
};

export default useLoginForm;
